import { Center, Icon } from '@tempots/beatui'
import { attr, html, on, OneOfValue, prop, Use } from '@tempots/dom'
import { Anchor, HTMLTitle, Location } from '@tempots/ui'
import { ApiError, Auth } from '@/services/auth-service'
import { RequireAnon } from '@/components/require-anon'
import { Track } from '@/services/track'

export function ConfirmEmailPage() {
  return RequireAnon({}, () =>
    Use(Location, location => {
      const status = prop<'pending' | 'error'>('pending')
      const message = prop<string | null>(null)

      const tokenSignal = location.search.$.token

      tokenSignal.on(
        async token => {
          if (!token) {
            status.set('error')
            message.set('This confirmation link is missing a token.')
            return
          }

          try {
            await Auth.verifyEmail(token)
            Track.auth({
              action: 'email_verification',
              message: 'Email verified',
            })
            location.navigate('/start', { replace: true })
          } catch (error) {
            Track.apiError({
              error_code: 'EMAIL_VERIFICATION_FAILED',
              error_message:
                error instanceof ApiError ? error.message : 'Unknown error',
            })
            status.set('error')
            if (error instanceof ApiError) {
              message.set(
                error.message ||
                  'We were unable to verify your email. The link may have expired.'
              )
            } else {
              message.set(
                'We were unable to verify your email. Please try requesting a new link.'
              )
            }
          }
        },
        { once: true }
      )

      return html.main(
        HTMLTitle('Confirm Email - ResumeHeaders'),
        attr.class('min-h-full flex items-center justify-center px-4'),
        OneOfValue(status, {
          pending: () =>
            Center(
              {},
              Icon(
                { icon: 'line-md:loading-twotone-loop' },
                attr.class('h-16 w-16 text-primary-500')
              )
            ),
          error: () =>
            html.div(
              attr.class(
                'flex flex-col items-center text-center gap-4 max-w-md'
              ),
              html.h1(
                attr.class('text-xl font-semibold text-gray-900'),
                'Email verification failed'
              ),
              html.p(
                attr.class('text-gray-600'),
                message.map(
                  msg =>
                    msg ??
                    'The verification link is invalid or has already been used.'
                )
              ),
              Anchor(
                { href: '/verify-email' },
                on.click(() => {
                  Track.click({
                    section: 'main',
                    element_type: 'button',
                    click_action: 'navigate',
                    element_id: 'verify_email',
                  })
                }),
                attr.class('text-primary-600 underline'),
                'Request a new verification link'
              )
            ),
        })
      )
    })
  )
}
